/**
 * Currency utility functions
 * Shared between frontend and backend
 */
/**
 * Get default currency for a locale
 * Maps locale codes to their default currency based on ISO 4217 standards
 * @param locale - The locale code (e.g., "en-US", "fr-FR", "en")
 * @returns The default currency code for the locale (e.g., "USD", "EUR")
 */
export function getDefaultCurrencyForLocale(locale) {
    // Map locale to default currency
    const localeToCurrency = {
        "en-US": "USD",
        "en-CA": "CAD",
        "en-GB": "GBP",
        "en-AU": "AUD",
        "en-NZ": "NZD",
        "en-IE": "EUR",
        "fr-FR": "EUR",
        "fr-BE": "EUR",
        "fr-CH": "CHF",
        "fr-CA": "CAD",
        "de-DE": "EUR",
        "de-AT": "EUR",
        "de-CH": "CHF",
        "it-IT": "EUR",
        "es-ES": "EUR",
        "es-MX": "MXN",
        "es-AR": "ARS",
        "pt-PT": "EUR",
        "pt-BR": "BRL",
        "nl-NL": "EUR",
        "nl-BE": "EUR",
        "ja-JP": "JPY",
        "zh-CN": "CNY",
        "zh-TW": "TWD",
        "ko-KR": "KRW",
        "ru-RU": "RUB",
        "pl-PL": "PLN",
        "sv-SE": "SEK",
        "no-NO": "NOK",
        "da-DK": "DKK",
        "fi-FI": "EUR",
        "cs-CZ": "CZK",
        "hu-HU": "HUF",
        "ro-RO": "RON",
        "tr-TR": "TRY",
        "th-TH": "THB",
        "vi-VN": "VND",
        "id-ID": "IDR",
        "ms-MY": "MYR",
        "hi-IN": "INR",
        "ar-SA": "SAR",
        "he-IL": "ILS",
        en: "USD",
        fr: "EUR",
        de: "EUR",
        it: "EUR",
        es: "EUR",
        pt: "EUR",
        nl: "EUR",
        ja: "JPY",
        zh: "CNY",
        ko: "KRW",
        ru: "RUB",
    };
    // Try full locale first (e.g., "en-US")
    if (localeToCurrency[locale]) {
        return localeToCurrency[locale];
    }
    // Try language code only (e.g., "en")
    const languageCode = locale.split("-")[0];
    if (localeToCurrency[languageCode]) {
        return localeToCurrency[languageCode];
    }
    // Default to EUR if no mapping found
    return "EUR";
}
/**
 * Validate currency code against ISO 4217 standard
 * @param currency - The currency code to validate (e.g., "USD", "EUR")
 * @returns true if the currency code is valid, false otherwise
 */
export function isValidCurrencyCode(currency) {
    // Comprehensive list of ISO 4217 currency codes supported by Intl API
    const validCurrencies = new Set([
        "USD",
        "EUR",
        "GBP",
        "JPY",
        "CNY",
        "AUD",
        "CAD",
        "CHF",
        "HKD",
        "NZD",
        "SEK",
        "NOK",
        "DKK",
        "PLN",
        "RUB",
        "INR",
        "BRL",
        "ZAR",
        "KRW",
        "MXN",
        "SGD",
        "THB",
        "TRY",
        "IDR",
        "MYR",
        "PHP",
        "CZK",
        "HUF",
        "RON",
        "ILS",
        "CLP",
        "ARS",
        "COP",
        "PEN",
        "VND",
        "TWD",
        "AED",
        "SAR",
        "QAR",
        "KWD",
        "BHD",
        "OMR",
        "JOD",
        "LBP",
        "EGP",
        "NGN",
        "GHS",
        "KES",
        "UGX",
        "TZS",
        "ETB",
        "MAD",
        "DZD",
        "TND",
        "XOF",
        "XAF",
        "ZMW",
        "BWP",
        "MZN",
        "MGA",
        "PKR",
        "BDT",
        "LKR",
        "NPR",
        "MMK",
        "KHR",
        "LAK",
        "BND",
        "KZT",
        "UZS",
        "KGS",
        "TJS",
        "AFN",
        "IRR",
        "IQD",
        "SYP",
        "YER",
        "AMD",
        "AZN",
        "GEL",
        "BYN",
        "MDL",
        "UAH",
        "BGN",
        "RSD",
        "BAM",
        "MKD",
        "ALL",
        "ISK",
        "HRK",
        "BBD",
        "BZD",
        "BSD",
        "BMD",
        "KYD",
        "XCD",
        "AWG",
        "ANG",
        "SRD",
        "GYD",
        "TTD",
        "JMD",
        "HTG",
        "CUP",
        "DOP",
        "PAB",
        "NIO",
        "CRC",
        "GTQ",
        "HNL",
        "SVC",
        "PYG",
        "UYU",
        "BOB",
        "VES",
        "FKP",
        "FJD",
        "PGK",
        "SBD",
        "VUV",
        "XPF",
    ]);
    return validCurrencies.has(currency.toUpperCase());
}
